import { useState } from 'react';
import { X, Plus, Heart, ThumbsUp, Meh, ThumbsDown, Loader, Sparkles } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabaseService } from '../../lib/supabase-service';
import { tmdbService } from '../../lib/tmdb-service';

const RATING_OPTIONS = [
  {
    value: 'loved',
    label: 'Loved',
    icon: Heart,
    iconColor: 'text-pink-600',
    bgColor: 'bg-pink-50',
    hoverBg: 'hover:bg-pink-100',
    borderColor: 'border-pink-200'
  },
  {
    value: 'liked',
    label: 'Liked',
    icon: ThumbsUp,
    iconColor: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    hoverBg: 'hover:bg-emerald-100',
    borderColor: 'border-emerald-200'
  },
  {
    value: 'neutral',
    label: 'Meh',
    icon: Meh,
    iconColor: 'text-amber-600',
    bgColor: 'bg-amber-50',
    hoverBg: 'hover:bg-amber-100',
    borderColor: 'border-amber-200'
  },
  {
    value: 'hated',
    label: 'Disliked',
    icon: ThumbsDown,
    iconColor: 'text-red-600',
    bgColor: 'bg-red-50',
    hoverBg: 'hover:bg-red-100',
    borderColor: 'border-red-200'
  }
];

export default function QuickAddSeenModal({ userId, authUserId, onClose, onAdd }) {
  const [title, setTitle] = useState('');
  const [year, setYear] = useState('');
  const [rating, setRating] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tmdbPreview, setTmdbPreview] = useState(null);
  const [resultIndex, setResultIndex] = useState(0);

  const handlePreviewMovie = async (index = 0) => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setLoading(true);

    try {
      const loadingToast = toast.loading('Searching TMDB...');

      const tmdbResult = await tmdbService.findOrEnrichMovie(
        title.trim(),
        year ? parseInt(year) : null,
        index
      );
      toast.dismiss(loadingToast);

      if (tmdbResult.tmdbFound) {
        setTmdbPreview(tmdbResult);
        setResultIndex(index);
        const resultInfo = tmdbResult.totalResults > 1
          ? ` (${index + 1} of ${tmdbResult.totalResults} results)`
          : '';
        toast.success(`Found: ${tmdbResult.title} (${tmdbResult.releaseYear})${resultInfo}`);
      } else {
        toast.error('Could not find movie on TMDB. Try adding the year to narrow the search.');
      }
    } catch (error) {
      console.error('Error searching TMDB:', error);
      toast.error('Failed to search TMDB');
    } finally {
      setLoading(false);
    }
  };

  const handleTryNextResult = () => {
    handlePreviewMovie(resultIndex + 1);
  };

  const handleAddMovie = async (selectedRating = null) => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setLoading(true);

    try {
      let movieYear;
      let tmdbData = {};

      if (tmdbPreview) {
        // Use preview data
        movieYear = tmdbPreview.releaseYear;
        tmdbData = {
          description: tmdbPreview.description,
          genre: tmdbPreview.genre,
          image: tmdbPreview.image,
          director: tmdbPreview.director,
          link_metadata: {
            tmdbId: tmdbPreview.tmdbId,
            tmdbRating: tmdbPreview.tmdbRating,
            imdbId: tmdbPreview.imdbId
          }
        };
      } else if (year) {
        // Manual year provided
        movieYear = parseInt(year);
      } else {
        toast.error('Please preview the movie first or enter year manually');
        setLoading(false);
        return;
      }

      // Use deduplication to find or create movie
      const result = await supabaseService.findOrCreateMovie(title.trim(), movieYear, {
        category: 'movie',
        created_by: authUserId,
        ...tmdbData
      });

      // Always mark as seen (rating is optional)
      const response = await fetch('/api/watch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'mark-as-seen',
          userId,
          entertainmentId: result.id,
          rating: selectedRating
        })
      });

      const data = await response.json();

      console.log('📊 Watch history response:', data);

      if (!data.success) {
        throw new Error('Failed to save watch history');
      }

      // Show appropriate toast
      const actionText = selectedRating ? 'added to watch history and rated' : 'added to watch history';
      if (result.isUpdate) {
        toast.success(`"${title}" was already in your list! ${selectedRating ? 'Rating saved.' : 'Marked as seen.'}`);
      } else {
        toast.success(`"${title}" ${actionText}!`);
      }

      // Notify parent component with movie data for optimistic update
      onAdd?.({
        movie: result,
        watchHistory: data.history,
        isNewMovie: !result.isUpdate
      });

      // Reset form for next entry
      setTitle('');
      setYear('');
      setRating(null);
      setTmdbPreview(null);
      setResultIndex(0);
    } catch (error) {
      console.error('Error adding movie:', error);
      toast.error('Failed to add movie');
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-1">Build Watch History</h2>
              <p className="text-white text-opacity-90 text-sm">
                Add movies you've already seen to improve AI recommendations
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Title Input */}
          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-2">
              Movie/Show Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setTmdbPreview(null); // Clear preview when title changes
                setResultIndex(0); // Reset to first result
              }}
              placeholder="e.g. A Quiet Place"
              className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={loading}
              autoFocus
            />
          </div>

          {/* Year Input (Optional) */}
          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-2">
              Release Year (Optional - helps narrow search)
            </label>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              placeholder={`e.g. ${currentYear}`}
              min="1900"
              max={currentYear + 1}
              className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={loading}
            />
          </div>

          {/* Search Button */}
          {!tmdbPreview && (
            <button
              onClick={() => handlePreviewMovie(0)}
              disabled={loading || !title.trim()}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Find Movie
                </>
              )}
            </button>
          )}

          {/* TMDB Preview Card */}
          {tmdbPreview && (
            <div className="bg-gradient-to-br from-indigo-50 to-violet-50 border-2 border-indigo-200 rounded-xl p-4">
              <div className="flex gap-4">
                {tmdbPreview.image && (
                  <img
                    src={tmdbPreview.image}
                    alt={tmdbPreview.title}
                    className="w-20 h-30 object-cover rounded-lg"
                  />
                )}
                <div className="flex-1">
                  <h3 className="font-bold text-stone-900">{tmdbPreview.title}</h3>
                  <p className="text-sm text-indigo-600 font-semibold">{tmdbPreview.releaseYear}</p>
                  {tmdbPreview.genre && (
                    <p className="text-xs text-stone-600 mt-1">{tmdbPreview.genre}</p>
                  )}
                  {tmdbPreview.description && (
                    <p className="text-xs text-stone-600 mt-2 line-clamp-2">{tmdbPreview.description}</p>
                  )}
                  {tmdbPreview.totalResults > 1 && (
                    <p className="text-xs text-indigo-500 font-medium mt-2">
                      Result {resultIndex + 1} of {tmdbPreview.totalResults}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                {tmdbPreview.totalResults > resultIndex + 1 && (
                  <button
                    onClick={handleTryNextResult}
                    disabled={loading}
                    className="flex-1 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    Try next result →
                  </button>
                )}
                <button
                  onClick={() => {
                    setTmdbPreview(null);
                    setResultIndex(0);
                  }}
                  disabled={loading}
                  className="flex-1 px-3 py-2 bg-white hover:bg-stone-50 text-stone-700 text-sm font-medium rounded-lg transition-colors border border-stone-200 disabled:opacity-50"
                >
                  Search again
                </button>
              </div>
            </div>
          )}

          {/* Rating Selection - Click to Add */}
          {tmdbPreview && (
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-2">
                Click a rating to add
              </label>
              <div className="grid grid-cols-4 gap-2">
                {RATING_OPTIONS.map(({ value, label, icon: Icon, iconColor, bgColor, hoverBg, borderColor }) => {
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleAddMovie(value)}
                      disabled={loading}
                      className={`flex flex-col items-center gap-1 px-2 py-3 ${bgColor} ${hoverBg} border ${borderColor} rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                      title={`Add and rate as ${label}`}
                    >
                      <Icon className={`w-5 h-5 ${iconColor}`} />
                      <span className="text-xs font-medium text-stone-700">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Close Button */}
          <div className="pt-2">
            <button
              type="button"
              onClick={onClose}
              className="w-full px-4 py-3 bg-stone-100 hover:bg-stone-200 text-stone-700 font-semibold rounded-lg transition-colors"
              disabled={loading}
            >
              Close
            </button>
          </div>
        </div>

        {/* Tips */}
        <div className="px-6 pb-6">
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-indigo-900 mb-2">💡 Quick Tips:</h3>
            <ul className="text-xs text-indigo-800 space-y-1">
              <li>• Type title → Click "Find Movie" → See instant preview with poster</li>
              <li>• Add year (optional) to narrow search for specific version</li>
              <li>• Wrong movie? Click "Try next result →" to browse other matches</li>
              <li>• Click a rating button to instantly add to your history</li>
              <li>• More history = better AI recommendations!</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
